import { EventBus } from '../plugins/event-bus'

export const getTagAllRegionsDisplayValue = (category) => {
  const map = {
    // News/all - use default value
    1: null,
    // Job
    2: 'Remote/All Regions',
    // Event
    3: 'Online',
  }

  return map[category] || null
}

/**
 * Helper function for checking whether a tag being displayed is an 'other' tag of any category
 * If it is, we need to figure out what the string value stored in additional_info is that was used when the other tag was set
 * @param {object} item  tag object
 * @param {JSON} additionalInfo json object or null
 */
export function getTagDisplayTitle(item, additionalInfo) {
  if (item.title === 'Other') {
    const categoryId = `category_id_${item.category}`
    if (additionalInfo && additionalInfo[categoryId]) {
      return additionalInfo[categoryId].other
    }
  }
  return item.title
}

export const pluraliseString = (str, count) => {
  if (count > 1) {
    return `${str}s`
  }
  return str
}

export const getDateFormatted = (date) => {
  return new Date(date).toLocaleDateString('en-NZ', {
    timeZone: 'UTC',
  })
}

/**
 * Valid values are integers above -1 (0 is allowed),
 * so we can't just check for any falsy value (undefined, false, null, 0)
 *
 * @param {*} value: some value to check
 * @returns {boolean} valid or not
 */
export const checkPositiveInteger = (value) => {
  return typeof value === 'number' && value > -1
}

export const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(email)
}

export const validateImageFormat = (file) => {
  let success = true

  let file_limit = 1024 * 1024 * 2 // 2mb
  let image_formats = /(\.png|\.jpg|\.jpeg|\.pdf)$/i

  let is_valid_file_format = image_formats.exec(file.name)
  let is_under_max_file_limit = file.size <= file_limit

  if (!is_valid_file_format || !is_under_max_file_limit) {
    let message = 'File size too large. 2 MB file size limit exceeded.'
    if (!is_valid_file_format) {
      message = 'Image format is incorrect.'
    }

    EventBus.$emit('triggerAlertResponse', {
      status: 'error',
      message: message,
    })

    success = false
  }

  return success
}

export const URL_PROTOCOLS_HTTP = {
  HTTP: 'http://',
  HTTPS: 'https://',
}

export const URL_PROTOCOLS = {
  ...URL_PROTOCOLS_HTTP,
  MAILTO: 'mailto:',
}

/**
 * Checks whether a given string is a valid url.
 * NOTE: String does NOT require a valid protocol (http, etc.) to pass,
 * unless it is a 'mailto' address. Protocol validation is handled separately.
 *
 * @param {string} url
 * @returns boolean string is a valid url or not
 */
export const validateURL = (url) => {
  const PATTERN_URL = new RegExp(
    '^(https?:\\/\\/)?' + // protocol (not required)
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ) // fragment locator

  const PATTERN_MAILTO = new RegExp(
    `^${URL_PROTOCOLS.MAILTO}` + // protocol (required for mailto/emails)
      '([a-zA-Z0-9._%+-]+)' + // username
      '@' + // @ symbol
      '(([a-zA-Z0-9-]+.)+[a-zA-Z]{2,})$', // domain
    'i',
  )

  const PATTERNS = [PATTERN_URL, PATTERN_MAILTO]
  return PATTERNS.some((pattern) => !!pattern.test(url))
}

/**
 * Checks whether a given string has a valid url protocol.
 *
 * @param {string} str
 * @returns boolean string has a valid url protocol or not
 */
export const validateURLProtocol = (url, protocols = null) => {
  protocols = protocols || URL_PROTOCOLS
  return Object.values(protocols).some((protocol) => url.startsWith(protocol))
}

/**
 * Updates a given url to start with a valid protocol.
 * Defaults to https:// if no type is provided.
 *
 * @param {string} url
 * @param {string} type url protocol type
 * @returns string url updated with valid protocol
 */
export const updateURLWithProtocol = (url, type) => {
  const PROTOCOL = URL_PROTOCOLS[type] || URL_PROTOCOLS.HTTPS

  return `${PROTOCOL}${url}`
}

export const isAdmin = (url) => {
  if (url.indexOf('/manager') > 0) {
    return true
  } else {
    return false
  }
}

export const parseURLParams = (url) => {
  let queryStart = url.indexOf('?') + 1,
    queryEnd = url.indexOf('#') + 1 || url.length + 1,
    query = url.slice(queryStart, queryEnd - 1),
    pairs = query.replace(/\+/g, ' ').split('&'),
    parms = {},
    i,
    n,
    v,
    nv

  if (query === url || query === '') {
    return false
  } // Return false if there are no URL get params

  for (i = 0; i < pairs.length; i++) {
    nv = pairs[i].split('=', 2)
    n = decodeURIComponent(nv[0])
    v = decodeURIComponent(nv[1])

    if (!parms.hasOwnProperty(n)) {
      parms[n] = []
    }
    parms[n].push(nv.length === 2 ? v : null)
  }

  return parms
}

export const parseAssessmentHashFromUrl = (url) => {
  let assHashStart = url.indexOf('?') + 1
  let assHashEnd = url.length + 1
  let assHash = url.slice(assHashStart, assHashEnd - 1)

  if (assHash === url || assHash === '') {
    return false
  } // Return false if there are no URL get params
  return assHash
}

export const getHasbang = (url) => {
  let hashStart = url.indexOf('#') + 1
  let hashEnd = url.length + 1
  let hash = url.slice(hashStart, hashEnd - 1)
  if (hash === url || hash === '') {
    return false
  } // Return false if there are no URL get params
  return hash
}

export const formatNumber = (val) => {
  return Number(val).toLocaleString()
}

export const isMenuItemLocked = (menuItem, settings) => {
  if (!menuItem || !settings) {
    return false
  }

  const updatesRequiredMenuMap = {
    'New Post': 'Tags',
    'Cloud': 'Tags',
  }

  menuItem = updatesRequiredMenuMap[menuItem] || menuItem
  return settings?.migrations_status?.missing?.includes(menuItem)
}

export const mapArrayToObjectByKey = (arr, key) => {
  key = key || 'id'
  return arr.reduce((acc, obj) => {
    const id = obj[key]
    if (!acc[id]) {
      acc[id] = obj
    }

    return acc
  }, {})
}

export const mapArrayStringsToInt = (arr) => {
  return arr.map((str) => parseInt(str))
}

export const toSnakeCase = (str) => {
  return str.replace(/([A-Z])/g, function ($1) {
    return '_' + $1.toLowerCase()
  })
}

export const convertKeysToSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((val) => convertKeysToSnakeCase(val))
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce(
      (acc, key) => ({
        ...acc,
        [toSnakeCase(key)]: convertKeysToSnakeCase(obj[key]),
      }),
      {},
    )
  }
  return obj
}

export const toCamelCase = (str) => {
  return str.replace(/([-_][a-z])/g, (group) =>
    group.toUpperCase().replace('-', '').replace('_', ''),
  )
}

export const convertKeysToCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((val) => convertKeysToCamelCase(val))
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce(
      (acc, key) => ({
        ...acc,
        [toCamelCase(key)]: convertKeysToCamelCase(obj[key]),
      }),
      {},
    )
  }
  return obj
}

export const toPascalCase = (str) => {
  return str
    .match(/[a-z]+/gi)
    .map(function (word) {
      return word.charAt(0).toUpperCase() + word.substr(1).toLowerCase()
    })
    .join('')
}

export const capitalizeFirstLetter = (str) => {
  return `${str.charAt(0).toUpperCase()}${str.slice(1)}`
}

export const routeToExternalLink = (url) => {
  window.open(url, '_blank')
}

export const scrollToElement = (elementId) => {
  const element = document.getElementById(elementId)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}
